import { __awaiter, __generator, __read, __rest, __spreadArray } from "tslib";
import React, { useCallback, useEffect, useRef } from 'react';
import { ChannelIO } from './ChannelIO';
import { ReactChannelIOContext } from './context';
import { createChannelIOEventDispatcher, REACT_CHANNELIO_EVENT_METHODS, } from './events';
import { debugLogger, scriptInjector, useCallbackProp, useDeepEffect, warnLogger, } from './utils';
/** URL of ChannelIO SDK. */
var PLUGIN_URL = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
/** Attribute name of custom launcher. */
var LAUNCHER_ATTR_NAME = 'data-channel-plugin';
/** Attribute value of custom launcher. */
var LAUNCHER_ATTR_VAL = 'launcher';
export var ReactChannelIO = function (_a) {
    var children = _a.children, _b = _a.autoBoot, autoBoot = _b === void 0 ? false : _b, _c = _a.autoBootTimeout, autoBootTimeout = _c === void 0 ? 1000 : _c, _d = _a.rebootOnOptionChanged, rebootOnOptionChanged = _d === void 0 ? true : _d, _e = _a.useCustomLauncherSelectorTweak, useCustomLauncherSelectorTweak = _e === void 0 ? true : _e, _f = _a.verbose, verbose = _f === void 0 ? false : _f, onBoot = _a.onBoot, channelIOBootOption = __rest(_a, ["children", "autoBoot", "autoBootTimeout", "rebootOnOptionChanged", "useCustomLauncherSelectorTweak", "verbose", "onBoot"]);
    var onBootRef = useCallbackProp(onBoot);
    var optionRef = useRef(channelIOBootOption);
    var _g = __read(React.useState(false), 2), isBooted = _g[0], setBooted = _g[1];
    /**
     * Make ready before plug in init.
     * - ref: https://developers.channel.io/docs/web-installation
     */
    var createPluginQueue = function () {
        if (window.ChannelIO) {
            return;
        }
        debugLogger(verbose, 'Creating plugin queue...');
        var ch = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            ch.c(args);
        };
        ch.q = [];
        ch.c = function (args) {
            ch.q.push(args);
        };
        window.ChannelIO = ch;
    };
    /**
     * Add event callbacks that dispatching internal events.
     */
    var addPluginEventCallbacks = function () {
        REACT_CHANNELIO_EVENT_METHODS.forEach(function (method) {
            ChannelIO(method, createChannelIOEventDispatcher(method));
        });
    };
    /**
     * ### `boot`
     *
     * Boot up channel plugin(button) to make it ready to use
     *
     * @link https://developers.channel.io/docs/web-channel-io#boot
     *
     * @param option a Boot Option object contains informations to initialize Channel IO plugin
     */
    var boot = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, new Promise(function (resolve, reject) {
                    try {
                        addPluginEventCallbacks();
                        debugLogger(verbose, 'Booting plugin...');
                        debugLogger(verbose, 'boot options =>', optionRef.current);
                        ChannelIO('boot', optionRef.current, function (err, user) {
                            if (typeof onBootRef.current === 'function') {
                                onBootRef.current(err, user);
                            }
                            if (err) {
                                warnLogger('Error occurred while boot plugin.', err);
                                setBooted(false);
                                reject(err);
                                return;
                            }
                            //
                            // === update user ===
                            // Need to update user since channel plugin has limitation
                            //
                            var updateUserData = {};
                            // Change user language, when user language of plugin different with option one.
                            // User language won't change, if the user already created.
                            // - ref: https://developers.channel.io/docs/mobile-models#bootconfig
                            if ((user === null || user === void 0 ? void 0 : user.language) !== optionRef.current.language) {
                                updateUserData.language = optionRef.current.language;
                            }
                            // Reset profile when `profile` set as `null`.
                            if (optionRef.current.profile === null) {
                                updateUserData.profile = null;
                            }
                            ChannelIO('updateUser', updateUserData, function (err, updatedUser) {
                                if (err) {
                                    warnLogger('Fail to reset user information of plugin.');
                                    return;
                                }
                                debugLogger(verbose, 'User updated after booting', updatedUser);
                            });
                            //
                            // === update user end ===
                            //
                            debugLogger(verbose, 'Booted');
                            setBooted(true);
                            resolve(user);
                        });
                    }
                    catch (err) {
                        reject(err);
                    }
                })];
        });
    }); }, [verbose, onBootRef]);
    /**
     * ### `shutdown`
     *
     * Shutdown channel plugin
     *
     * @link https://developers.channel.io/docs/web-channel-io#shutdown
     */
    var shutdown = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        debugLogger(verbose, 'Shuting down plugin...');
        ChannelIO('clearCallbacks');
        ChannelIO.apply(void 0, __spreadArray(['shutdown'], __read(args)));
        setBooted(false);
    }, [verbose]);
    //
    // Bootstrap plugin.
    //
    useEffect(function () {
        debugLogger(verbose, 'Bootstraping plugin...', '(Set verbose flag `false`, to remove these debugging logs)');
        void (function () { return __awaiter(void 0, void 0, void 0, function () {
            var err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 5, , 6]);
                        createPluginQueue();
                        return [4 /*yield*/, scriptInjector(PLUGIN_URL, verbose)];
                    case 1:
                        _a.sent();
                        debugLogger(verbose, 'Auto boot flag set as', autoBoot);
                        if (!autoBoot) return [3 /*break*/, 4];
                        return [4 /*yield*/, new Promise(function (r) { return setTimeout(r, autoBootTimeout); })];
                    case 2:
                        _a.sent();
                        debugLogger(verbose, 'Auto Booting...');
                        return [4 /*yield*/, boot().catch(function () { return void 0; })];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4: return [3 /*break*/, 6];
                    case 5:
                        err_1 = _a.sent();
                        warnLogger('Error occurred at plugin boot processing on first mount.', err_1);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        }); })();
        return function () {
            shutdown();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //
    //
    //
    useEffect(function () {
        optionRef.current = channelIOBootOption;
    }, [channelIOBootOption]);
    //
    // Re-boot channel plugin when boot option changed.
    //
    useDeepEffect(function () {
        if (isBooted && rebootOnOptionChanged) {
            debugLogger(verbose, 'Rebooting since option has been changed...');
            void boot().catch(function () { return void 0; });
        }
    }, [channelIOBootOption]);
    //
    // Since ChannelIO does not support `customLauncherSelector` after plugin booted,
    // add onClick event listener at element which has `customLauncherSelector`
    // whenever DOM tree mutated. (observed by `MutationObserver`)
    //
    useEffect(function () {
        var customLauncherSelector = channelIOBootOption.customLauncherSelector;
        if (!useCustomLauncherSelectorTweak || !customLauncherSelector) {
            return;
        }
        var observer = new MutationObserver(function (mutationsList) {
            mutationsList
                .filter(function (mutation) { return mutation.type === 'childList'; })
                .filter(function (mutation) { return mutation.target instanceof HTMLElement; })
                .map(function (mutation) {
                return mutation.target.querySelectorAll(customLauncherSelector);
            })
                .forEach(function (customLauncherEls) {
                return Array.from(customLauncherEls)
                    .filter(function (customLauncherEl) { return customLauncherEl instanceof HTMLElement; })
                    .forEach(function (customLauncherEl) {
                    var el = customLauncherEl;
                    if (el.getAttribute(LAUNCHER_ATTR_NAME) === LAUNCHER_ATTR_VAL) {
                        return;
                    }
                    el.setAttribute(LAUNCHER_ATTR_NAME, LAUNCHER_ATTR_VAL);
                    el.addEventListener('click', function () { return ChannelIO('showMessenger'); }, false);
                });
            });
        });
        observer.observe(document.body, {
            attributes: false,
            childList: true,
            subtree: true,
        });
        return function () {
            observer.disconnect();
        };
    }, [
        channelIOBootOption.customLauncherSelector,
        useCustomLauncherSelectorTweak,
    ]);
    //
    //
    //
    return (React.createElement(ReactChannelIOContext.Provider, { value: { isBooted: isBooted, boot: boot, shutdown: shutdown } }, children));
};
